import { IPublicClientApplication, AuthError, AuthenticationResult } from '@azure/msal-browser';

/**
 * Safely initiates a login redirect.
 * Does NOT manipulate MSAL's internal storage -- MSAL manages its own state.
 */
export const safeLoginRedirect = async (
  instance: IPublicClientApplication,
  loginRequest: any
): Promise<void> => {
  try {
    await instance.loginRedirect({
      ...loginRequest,
      redirectUri: window.location.origin,
    });
  } catch (error: any) {
    const authError = error as AuthError;

    if (authError.errorCode === 'interaction_in_progress') {
      console.warn('Login interaction already in progress, please wait...');
    } else {
      console.error('Login redirect failed:', authError.errorCode, authError.message);
    }

    throw error;
  }
};

/**
 * Handles MSAL redirect promise with basic retry for transient failures.
 * NOTE: With MsalProvider handling redirects automatically in msal-react v3+,
 * this is typically only needed if called before MsalProvider mounts.
 */
export const safeHandleRedirectPromise = async (
  instance: IPublicClientApplication,
  maxRetries: number = 1
): Promise<AuthenticationResult | null> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await instance.handleRedirectPromise();
      return response;
    } catch (error) {
      const authError = error as AuthError;

      const nonRetryableErrors = [
        'user_cancelled',
        'consent_required',
        'invalid_client',
        'invalid_grant'
      ];

      if (authError.errorCode && nonRetryableErrors.includes(authError.errorCode)) {
        throw error;
      }

      if (attempt < maxRetries) {
        const delay = 500 * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }

  return null;
};
